import { createSelector, Selector } from 'reselect'

import { networkGroup } from './utils'

import authSelectors from './auth'
import routerSelectors from './router'

import {
  IRecord_Project,
} from '@kaidam/fc-shared/src/types/schema/project'

import {
  IRecord_User,
} from '@kaidam/fc-shared/src/types/schema/user'

import {
  IData_UserSummary,
} from '@kaidam/fc-shared/src/types/schema/user_summary'

import {
  INode_Org,
} from '@kaidam/fc-shared/src/types/schema/tree'

import {
  IMeta_ProjectConfig_Palette,
} from '@kaidam/fc-shared/src/types/schema/project_config'

import {
  IMeta_OrgAppConfig,
} from '@kaidam/fc-shared/src/types/schema/app_config'

import {
  RootState,
} from '../../types/store'

import {
  APP_TITLE,
  APP_LOGO,
  APP_PALETTE,
} from '../../settings'

import {
  getAppMenu,
  getDashboardMenu,
  getMainMenu,
} from '../../menus'

export const data: Selector<RootState, IRecord_Project[]> = state => state.project.data
export const currentProjectId: Selector<RootState, string | null> = state => state.project.currentProjectId
export const currentOrgId: Selector<RootState, string | null> = state => state.project.currentOrgId
export const summary: Selector<RootState, IData_UserSummary | null> = state => state.project.summary

export const currentProject: Selector<RootState, IRecord_Project | null> = createSelector(
  data,
  currentProjectId,
  (data, id) => data.find(p => p.id == id) || null
)

export const needVerifying: Selector<RootState, boolean> = createSelector(
  currentProject,
  authSelectors.data,
  (project: IRecord_Project | null, user: IRecord_User | null) => {
    if(!project || !user) return false
    if(project.mode == 'manual') return false
    return project.verified_at ? false : true
  },
)

export const currentOrg: Selector<RootState, INode_Org | null> = createSelector(
  summary,
  (summary) => {
    if(!summary) return null
    return summary.orgNode
  },
)

export const summaryOrgId: Selector<RootState, string> = createSelector(
  currentOrg,
  (org) => {
    if(!org) return ''
    return org.id
  },
)

export const userOrgs: Selector<RootState, INode_Org[]> = createSelector(
  summary,
  (summary) => {
    if(!summary) return null
    return summary.orgNodes
  },
)

export const descendentOrgs: Selector<RootState, INode_Org[]> = createSelector(
  currentOrg,
  (orgNode): INode_Org[] => {
    if(!orgNode) return []
    const ret: INode_Org[] = []
    const collectOrg = (org: INode_Org) => {
      ret.push(org)
      org.orgs.forEach(collectOrg)
    }
    orgNode.orgs.forEach(collectOrg)
    return ret
  },
)

export const descendentOrgsWithOrg: Selector<RootState, INode_Org[]> = createSelector(
  currentOrg,
  descendentOrgs,
  (orgNode, descendentOrgs): INode_Org[] => {
    if(!orgNode) return []
    return [orgNode].concat(descendentOrgs)
  },
)

export const currentOrgMap = createSelector(
  currentOrg,
  org => {
    const idMap: Record<string, INode_Org> = {}
    if(!org) return idMap
    const collectOrg = (o: INode_Org) => {
      idMap[o.id] = o
      o.orgs.forEach(collectOrg)
      o.adjacentOrgs.forEach(collectOrg)
    }
    collectOrg(org)
    return idMap
  }
)

export const viewingOrg = createSelector(
  currentOrgMap,
  routerSelectors.params,
  (orgMap, params) => {
    return orgMap[params.orgid]
  }
)

export const title = createSelector(
  currentProject,
  (project): string => project && project.meta && project.meta.title ?
    project.meta.title :
    APP_TITLE,
)

export const logo = createSelector(
  currentProject,
  (project): string => project && project.meta && project.meta.logo ?
    project.meta.logo :
    '',
)

export const palette = createSelector(
  currentProject,
  (project): IMeta_ProjectConfig_Palette => project && project.meta && project.meta.palette ?
    project.meta.palette :
    APP_PALETTE,
)

export const appMenu = createSelector(
  authSelectors.data,
  summary,
  currentProject,
  (user, summary, project) => getAppMenu(user, summary, project),
)

export const dashboardMenu = createSelector(
  authSelectors.data,
  summary,
  currentProject,
  (user, summary, project) => getDashboardMenu(user, summary, project),
)

export const externalLinksMenu = createSelector(
  currentProject,
  (project) => project ? project.meta.external_links : [],
)

export const mainMenu = createSelector(
  authSelectors.data,
  summary,
  currentProject,
  (user, summary, project) => getMainMenu(user, summary, project),
)

export const appConfig = createSelector(  
  currentProject,
  (project) => {
    if(!project || !project.meta || !project.meta.apps) return []
    return project.meta.apps
  }
)

const defaultOrgAppConfig: IMeta_OrgAppConfig = {}

export const orgAppConfig = createSelector(  
  summary,
  (summary) => {
    return summary.orgNode.meta?.appconfig || defaultOrgAppConfig
  }
)

export const allOrgAppConfig = createSelector(
  currentOrgMap,
  (orgMap) => {
    const ret: Record<string, IMeta_OrgAppConfig> = {}
    for(const orgId in orgMap) {
      const org = orgMap[orgId]
      ret[orgId] = org.meta?.appconfig || defaultOrgAppConfig
    }
    return ret
  }
)

export const projectInfo = createSelector(  
  currentProjectId,
  currentOrgId,
  (project, org) => ({
    project,
    org,
  })
)

export const network = networkGroup('project', [
  'list',
])

export const sortedProjects = createSelector(  
  data,
  (projects) => {
    const returnProjects: IRecord_Project[] = [].concat(projects)
    returnProjects.sort((a, b) => {
      if(a.name < b.name) return -1
      if(a.name > b.name) return 1
      return 0
    })
    return returnProjects
  }
)


export default {
  data,
  currentProjectId,
  currentOrgId,
  summaryOrgId,
  currentOrgMap,
  descendentOrgs,
  descendentOrgsWithOrg,
  summary,
  currentProject,
  needVerifying,
  currentOrg,
  viewingOrg,
  userOrgs,
  title,
  logo,
  palette,
  appMenu,
  dashboardMenu,
  externalLinksMenu,
  mainMenu,
  appConfig,
  orgAppConfig,
  allOrgAppConfig,
  projectInfo,
  network,
  sortedProjects,
}
